import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, CheckCircle, ShieldCheck } from '@phosphor-icons/react';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui';
import { useAuth } from '@/lib/hooks/useAuth';
import { cn } from '@/lib/utils/cn';

const GUARANTEES: ReadonlyArray<string> = [
  'Free forever — every template, every feature',
  'Custom subdomain the moment you sign up',
  'Your work, your data — export anytime',
];

export function AuthPage() {
  const { signInWithProvider } = useAuth();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const next = params.get('next');

  async function onGoogle() {
    setLoading(true);
    setError(null);
    try {
      signInWithProvider('google', next ?? undefined);
    } catch {
      setLoading(false);
      setError('Could not reach the sign-in service. Try again in a moment.');
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-1 bg-canvas text-ink lg:grid-cols-[1.05fr_1fr]">
      <aside className="relative hidden overflow-hidden border-r border-line bg-board lg:flex lg:flex-col">
        <div className="mesh-bg pointer-events-none absolute inset-0 opacity-60" aria-hidden />
        <header className="relative z-10 flex items-center justify-between px-10 pt-10">
          <Link to="/" aria-label="Portfoli home">
            <Logo />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wide text-ink-muted transition-colors hover:text-ink"
          >
            Back to site
            <ArrowUpRight size={12} weight="bold" />
          </Link>
        </header>

        <div className="relative z-10 flex flex-1 flex-col justify-center px-10 py-16">
          <span className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-line bg-canvas px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Open for creators
          </span>
          <h1 className="mb-6 max-w-md font-display text-4xl font-semibold leading-tight tracking-tight text-ink">
            The portfolio you would have paid for —{' '}
            <span className="font-editorial italic text-ink-muted">at no cost.</span>
          </h1>
          <p className="mb-10 max-w-md text-[15px] leading-relaxed text-ink-muted">
            Sign in with Google to claim your subdomain, pick a template, and publish a
            structural, typographic home for your work.
          </p>
          <ul className="space-y-3">
            {GUARANTEES.map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm text-ink">
                <CheckCircle weight="fill" size={18} className="shrink-0 text-ink" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <footer className="relative z-10 flex items-center justify-between border-t border-line px-10 py-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
          <span>Trusted by 12k+ independents</span>
          <span>v1.0 · edge network</span>
        </footer>
      </aside>

      <section className="flex flex-col">
        <header className="flex items-center justify-between px-6 pt-6 lg:hidden">
          <Link to="/" aria-label="Portfoli home">
            <Logo />
          </Link>
          <Link
            to="/"
            className="font-mono text-xs uppercase tracking-wide text-ink-muted hover:text-ink"
          >
            Back to site
          </Link>
        </header>

        <div className="flex flex-1 items-center justify-center px-6 py-16">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faint">
                Step 01 · Sign in
              </p>
              <h2 className="mb-2 font-display text-3xl font-semibold tracking-tight text-ink">
                Welcome.
              </h2>
              <p className="text-sm text-ink-muted">
                Sign in with Google. We never ask for passwords.
              </p>
            </div>

            <Button
              variant="secondary"
              size="lg"
              fullWidth
              disabled={loading}
              onClick={onGoogle}
              leadingIcon={<GoogleMark />}
              className="h-12 rounded-lg text-[15px] font-medium"
            >
              {loading ? 'Opening Google…' : 'Continue with Google'}
            </Button>

            {error ? (
              <p className="mt-4 rounded-md border border-red-500/30 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </p>
            ) : null}

            {next ? (
              <p className="mt-3 text-center text-[11px] text-ink-faint">
                After signing in we'll take you back to where you left off.
              </p>
            ) : null}

            <div className="my-8 flex items-center gap-3">
              <span className="h-px flex-1 bg-line" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-faint">
                Why Google
              </span>
              <span className="h-px flex-1 bg-line" />
            </div>

            <div
              className={cn(
                'flex gap-3 rounded-lg border border-line bg-board p-4 text-[13px] leading-relaxed text-ink-muted',
              )}
            >
              <ShieldCheck
                weight="duotone"
                size={20}
                className="mt-0.5 shrink-0 text-ink"
              />
              <p>
                Google OAuth keeps credentials with them, not us. No password resets, no
                breach risk — your account is as safe as your Google account.
              </p>
            </div>

            <p className="mt-10 text-center text-xs text-ink-faint">
              By continuing you agree to the{' '}
              <a href="#" className="underline underline-offset-2 hover:text-ink">
                Terms
              </a>{' '}
              and{' '}
              <a href="#" className="underline underline-offset-2 hover:text-ink">
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>

        <footer className="border-t border-line px-6 py-4 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
          © {new Date().getFullYear()} Portfoli Systems
        </footer>
      </section>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      aria-hidden
      className="shrink-0"
    >
      <path
        fill="#EA4335"
        d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4-5.5 4-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.3 14.7 2.3 12 2.3 6.9 2.3 2.8 6.4 2.8 11.5S6.9 20.7 12 20.7c6.9 0 9.1-4.8 9.1-7.3 0-.5 0-.9-.1-1.3H12z"
      />
      <path
        fill="#4285F4"
        d="M21.1 12.1c0-.5 0-.9-.1-1.3H12v3.9h5.5c-.2 1.3-1 2.4-2.2 3.1l3.5 2.7c2-1.9 3.3-4.7 3.3-8.4z"
      />
      <path
        fill="#FBBC05"
        d="M5.3 14.1a6 6 0 010-4.1L1.8 7.3A9.4 9.4 0 002.8 11.5c0 1.5.4 2.9 1 4.1l3.5-2.7-2-.8z"
      />
      <path
        fill="#34A853"
        d="M12 20.7c2.7 0 4.9-.9 6.5-2.4l-3.5-2.7c-.9.6-2.1 1-3 1-2.3 0-4.2-1.5-4.9-3.6L3.6 15.7c1.5 3 4.6 5 8.4 5z"
      />
    </svg>
  );
}
